import React, { ReactNode, useEffect, useState } from 'react';

import { lucidColors, Link, isBrowser, LucidContainer } from '@lucid/gatsby-theme-lucid';
import { Box } from 'lucid-components-react';

import { Text } from 'theme/typography';
import { LucidTheme } from 'types/types';

interface Props {
  type?: 'alert' | 'warning' | 'notice' | 'white';
  storageKey: string;
  // TODO Add ability to choose localStorage or sessionStorage or cookie based on need of alert
  storageType?: 'local' | 'session' | 'cookie';
  children: ReactNode;
  linkUrl?: string;
  linkText?: string;
  condition?: boolean;
  dismissOnLinkClick?: boolean;
}

const COLORS = {
  alert: lucidColors.red[50],
  warning: lucidColors.yellow[10],
  notice: lucidColors.green[20],
  white: lucidColors.white
};

const AlertBanner = ({
  type = 'white',
  storageKey,
  condition = true,
  children,
  linkUrl,
  linkText,
  dismissOnLinkClick = true
}: Props) => {
  const [close, setClose] = useState(false);

  useEffect(() => {
    if (isBrowser()) {
      const sessionValue = sessionStorage.getItem(storageKey);
      setClose(!!sessionValue);
    }
  }, [storageKey]);

  useEffect(() => {
    if (isBrowser() && close) {
      sessionStorage.setItem(storageKey, 'closed');
    }
  }, [close, storageKey]);

  if (!condition || close) {
    return null;
  }

  return (
    <Box
      css={(theme: LucidTheme) => ({
        backgroundColor: COLORS[type],
        position: 'sticky',
        bottom: '0',
        left: '0',
        right: '0',
        width: '100%',
        padding: theme.space[3],
        zIndex: theme.zIndices[3]
      })}
    >
      <LucidContainer
        flex
        css={{ justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Box
          alignItems="baseline"
          css={(theme: LucidTheme) => ({ marginRight: theme.space[6] })}
          display="flex"
          flexDirection="row"
        >
          <Text
            css={(theme: LucidTheme) => ({ marginRight: theme.space[4] })}
            textStyles="bodySmall"
          >
            {children}
          </Text>
          {linkUrl && (
            <Link
              css={(theme: LucidTheme) => ({
                whiteSpace: 'nowrap',
                '&:hover': { color: `${theme.colors.text} !important` }
              })}
              onClick={() => {
                dismissOnLinkClick ? setClose(true) : null;
              }}
              to={linkUrl}
              underline="always"
            >
              {linkText || 'Read more.'}
            </Link>
          )}
        </Box>

        <Box
          css={(theme: LucidTheme) => ({ color: theme.colors.primaryText })}
          onClick={() => setClose(true)}
        >
          <button
            css={(theme: LucidTheme) => ({
              background: 'none',
              border: `1px solid ${COLORS[type]}`,
              cursor: 'pointer',
              height: '35px',
              width: '35px',
              fontSize: theme.fontSizes[7],
              fontWeight: theme.fontWeights.light,
              lineHeight: 0
            })}
          >
            ×
          </button>
        </Box>
      </LucidContainer>
    </Box>
  );
};

export default AlertBanner;
